import React, { useState, useEffect } from "react"
import { Modal, Button, Form } from "react-bootstrap"
import Select from "react-select"
import { useForm, Controller } from "react-hook-form"
import { PutRequest } from "../../Utils/hooks/ApiHandler"
import { toast } from "sonner"
import { getCountries, getStates, getCities } from "../../Utils/hooks/location"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen } from "@fortawesome/free-solid-svg-icons"
import { customStyles } from "../../Utils/helpers/commonhelper"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials"

const addressModalSchema = z.object({
  address: z
    .string()
    .min(1, "Address is required")
    .max(250, "Address is too long"),

  country: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .refine((val) => val !== null, {
      message: "Country is required",
    }),

  state: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .refine((val) => val !== null, {
      message: "State is required",
    }),

  city: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .refine((val) => val !== null, {
      message: "City is required",
    }),

  zipcode: z.string().min(1, "Zipcode is required"),
})

const AddressModal = ({
  show,
  handleClose,
  title,
  contactOption,
  setSelectedContact,
  defaultFor,
  onSubmit,
}) => {
  // getting essentials for apis
  const { customer, setCustomer, headers, extras, navigate, customerId, loginUserId, userType } = useGetEssentials()

  const [showAddForm, setShowAddForm] = useState(false)

  // Dynamic states
  const [countries, setCountries] = useState([])
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [isEditing, setIsEditing] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedState, setSelectedState] = useState(null)
  const [selectedCity, setSelectedCity] = useState(null)
  const [editIndex, setEditIndex] = useState(null)
  const [pendingEditAddress, setPendingEditAddress] = useState(null)

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(addressModalSchema),
  })
  // Fetch countries
  useEffect(() => {
    ; (async () => {
      try {
        const data = await getCountries()
        if (!data) return
        setCountries(
          data.map((country) => ({
            value: country.id,
            label: country.name,
          }))
        )
      } catch (error) {
        toast.error("Error fetching countries")
      }
    })()
  }, [])

  // Fetch states when country changes
  useEffect(() => {
    ; (async () => {
      if (!selectedCountry) {
        setStates([])
        return
      }
      try {
        const data = await getStates(selectedCountry.value)
        if (!data) return
        setStates(
          data.map((state) => ({
            value: state.id,
            label: state.state_name,
          }))
        )
      } catch (error) {
        toast.error("Error fetching states")
      }
    })()
  }, [selectedCountry])

  useEffect(() => {
    if (!isEditing || !pendingEditAddress?.state) return
    if (!states?.length) return

    const matchedState = states.find(
      (s) =>
        s.label?.trim().toLowerCase() ===
        pendingEditAddress.state?.trim().toLowerCase()
    )

    if (matchedState) {
      setSelectedState(matchedState)
      setValue("state", matchedState)
    }
  }, [states, isEditing, pendingEditAddress?.state])

  // Fetch cities when state changes
  useEffect(() => {
    ; (async () => {
      if (!selectedState) {
        setCities([])
        return
      }
      try {
        const data = await getCities(selectedState.value)
        if (!data) return
        setCities(
          data.map((city) => ({
            value: city.id,
            label: city.city_name,
          }))
        )
      } catch (error) {
        toast.error("Error fetching cities")
      }
    })()
  }, [selectedState])

  useEffect(() => {
    if (!isEditing || !pendingEditAddress?.city) return
    if (!cities?.length) return

    const matchedCity = cities.find(
      (c) =>
        c.label?.trim().toLowerCase() ===
        pendingEditAddress.city?.trim().toLowerCase()
    )

    if (matchedCity) {
      setSelectedCity(matchedCity)
      setValue("city", matchedCity)
      setPendingEditAddress(null)
    }
  }, [cities, isEditing, pendingEditAddress?.city])

  // Addresses list
  const [addresses, setAddresses] = useState([])
  useEffect(() => {
    if (defaultFor === "billing") {
      console.log(contactOption?.billingAddresses, "saurabh")
      setAddresses(contactOption?.billingAddresses || [])
    } else {
      setAddresses(contactOption?.shippingAddresses || [])
    }
  }, [contactOption, defaultFor])

  // Select Address handler
  const handleSelectAddress = async (addr, selectedIndex) => {
    let updatedAddresses = []

    if (defaultFor === "billing") {
      updatedAddresses = addresses?.map((item, i) => ({
        address: item?.address,
        country: item?.country,
        state: item?.state,
        city: item?.city,
        zipcode: item?.zipcode,
        selected: i === selectedIndex ? "selected" : "",
        for: "",
      }))
    }

    if (defaultFor === "shipping") {
      updatedAddresses = addresses?.map((item, i) => ({
        address: item?.address,
        country: item?.country,
        state: item?.state,
        city: item?.city,
        zipcode: item?.zipcode,
        selected: item?.selected || "",
        for: i === selectedIndex ? "shipping" : "",
      }))
    }

    const payload = {
      company_id: contactOption?.company_id,
      contact_id: contactOption?.contact_id ? contactOption?.contact_id : contactOption?.value,
      billing_address: updatedAddresses,
    }

    const res = await PutRequest(
      `/quotation/createBillingAddress/`,
      payload,
      { headers },
      extras
    )

    if (res.status === "success") {
      toast.success(res.message)
      let updatedList = []
      try {
        updatedList = JSON.parse(res.data.billing_address)
      } catch (err) {
        console.error("Error parsing address list:", err)
      }

      setAddresses(updatedList)
      setSelectedContact((prev) => ({
        ...prev,
        [defaultFor === "billing" ? "billingAddresses" : "shippingAddresses"]:
          updatedList,
      }))

      handleClose(true)
    }
  }

  // Form submit (add new)
  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      country: selectedCountry?.label || "",
      state: selectedState?.label || "",
      city: selectedCity?.label || "",
      for: defaultFor,
    })
    resetAddressForm()
    setShowAddForm(false)
  }
  const resetAddressForm = () => {
    reset({
      address: "",
      country: null,
      state: null,
      city: null,
      zipcode: "",
    })
    setSelectedCountry(null)
    setSelectedState(null)
    setSelectedCity(null)
    setStates([])
    setCities([])
    setEditIndex(null)
    setIsEditing(false)
    setPendingEditAddress(null)
  }

  // Edit Address
  const handleEditAddress = (addr, i) => {
    setEditIndex(i)
    setIsEditing(true)

    reset({
      address: addr?.address,
      country: { label: addr?.country, value: addr?.country_id || "" },
      state: { label: addr?.state, value: addr?.state_id || "" },
      city: { label: addr?.city, value: addr?.city_id || "" },
      zipcode: addr?.zipcode,
    })
    const matchedCountry = countries.find(
      (c) =>
        c.label?.trim().toLowerCase() === addr?.country?.trim().toLowerCase()
    )
    setSelectedCountry(
      addr?.country
        ? matchedCountry || { label: addr?.country, value: addr?.country_id || "" }
        : null
    )
    setSelectedState(
      addr?.state ? { label: addr?.state, value: addr?.state_id || "" } : null
    )
    setSelectedCity(
      addr?.city ? { label: addr?.city, value: addr?.city_id || "" } : null
    )
    setPendingEditAddress({ state: addr?.state, city: addr?.city })

    setShowAddForm(true)
  }

  // Save edited address
  const handleEditSave = async (data) => {
    if (editIndex === null) return

    const updatedAddresses = addresses.map((item, i) =>
      i === editIndex
        ? {
          ...item,
          address: data?.address,
          country: data?.country?.label || data?.country || "",
          state: data?.state?.label || data?.state || "",
          city: data?.city?.label || data?.city || "",
          zipcode: data?.zipcode,
        }
        : item
    )

    const payload = {
      company_id: contactOption?.company_id,
      contact_id: contactOption?.contact_id ? contactOption?.contact_id : contactOption?.value,
      billing_address: updatedAddresses,
    }

    const res = await PutRequest(
      `/quotation/createBillingAddress/`,
      payload,
      { headers },
      extras
    )

    if (res.status === "success") {
      toast.success(res.message)
      let updatedList = []
      try {
        updatedList = JSON.parse(res?.data?.billing_address)
      } catch (err) {
        console.error("Error parsing address list:", err)
      }

      setAddresses(updatedList)
      setSelectedContact((prev) => ({
        ...prev,
        [defaultFor === "billing" ? "billingAddresses" : "shippingAddresses"]:
          updatedList,
      }))

      setShowAddForm(false)
      resetAddressForm()
    }
  }

  return (
    <>
      {" "}
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {defaultFor === "billing"
              ? "Billing Addresses"
              : "Shipping Addresses"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!showAddForm ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Select Address </h6>
                <Button
                  className="btn btn-default"
                  size="sm"
                  onClick={() => {
                    resetAddressForm()
                    setShowAddForm(true)
                  }}
                >
                  + Add Address
                </Button>
              </div>

              {addresses?.length > 0 ? (
                addresses?.map((addr, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded"
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="d-flex align-items-start flex-grow-1"
                      onClick={() => handleSelectAddress(addr, i)}
                    >
                      <Form.Check
                        type="radio"
                        name="selectedAddress"
                        checked={
                          defaultFor === "billing"
                            ? addr?.selected === "selected"
                            : addr?.for === "shipping"
                        }
                        readOnly
                        style={{ width: "24px", height: '24px' }}
                        className="me-3 mt-1 custom-radio"
                      />
                      <div>
                        <p className="mb-0 fs-13 fw-500">{addr?.address}</p>
                        <p className="mb-0 fs-13 text-dark">
                          {typeof addr?.city === "object"
                            ? addr?.city?.label
                            : addr?.city}
                          ,{" "}
                          {typeof addr?.state === "object"
                            ? addr?.state?.label
                            : addr?.state}
                          ,{" "}
                          {typeof addr?.country === "object"
                            ? addr?.country?.label
                            : addr?.country}{" "}
                          - {addr?.zipcode}
                        </p>
                      </div>
                    </div>

                    <FontAwesomeIcon
                      icon={faPen}
                      style={{ color: "#6c757d", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEditAddress(addr, i)
                      }}
                    />
                  </div>
                ))
              ) : (
                <p className="text-muted fs-13">
                  No {defaultFor} address available
                </p>
              )}
            </>
          ) : (
            <Form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="form-container"
               onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                  e.preventDefault();
                }
              }}
            >
              {/* Address */}
              <Form.Group className="mb-3 focus-z-index position-relative">
                <Form.Label>
                  Address <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  type="text"
                  placeholder="Enter Address"
                  {...register("address", { required: true })}
                />
                {errors?.address && (
                  <p className="text-danger">{errors?.address?.message}</p>
                )}
              </Form.Group>

              {/* Country */}
              <Form.Group className="mb-3 focus-z-index position-relative">
                <Form.Label>
                  Country <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      {...field}
                      options={countries}
                      placeholder="Select Country"
                      classNamePrefix="react-select"
                      onChange={(val) => {
                        field.onChange(val)
                        setSelectedCountry(val)
                        setSelectedState(null)
                        setSelectedCity(null)
                        setStates([])
                        setCities([])
                        setValue("state", null)
                        setValue("city", null)
                      }}
                    />
                  )}
                />
                {errors?.country && (
                  <p className="text-danger">Country is required</p>
                )}
              </Form.Group>

              {/* State */}
              <Form.Group className="mb-3 focus-z-index position-relative">
                <Form.Label>
                  State <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      {...field}
                      options={states}
                      placeholder="Select State"
                      classNamePrefix="react-select"
                      onChange={(val) => {
                        field.onChange(val)
                        setSelectedState(val)
                        setSelectedCity(null)
                        setCities([])
                        setValue("city", null)
                      }}
                      isDisabled={!selectedCountry}
                    />
                  )}
                />
                {errors?.state && (
                  <p className="text-danger">State is required</p>
                )}
              </Form.Group>

              {/* City */}
              <Form.Group className="mb-3 focus-z-index position-relative">
                <Form.Label>
                  City <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      {...field}
                      options={cities}
                      classNamePrefix="react-select"
                      placeholder="Select City"
                      onChange={(val) => {
                        field.onChange(val)
                        setSelectedCity(val)
                      }}
                      isDisabled={!selectedState}
                    />
                  )}
                />
                {errors?.city && (
                  <p className="text-danger">City is required</p>
                )}
              </Form.Group>

              {/* Zipcode */}
              <Form.Group className="mb-3 focus-z-index position-relative">
                <Form.Label>
                  Zipcode <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Zipcode"
                  {...register("zipcode", { required: true })}
                />
                {errors?.zipcode && (
                  <p className="text-danger">{errors?.zipcode?.message} </p>
                )}
              </Form.Group>

              <Modal.Footer>
                <button
                  className="btn btn-default-outline"
                  onClick={() => {
                    setShowAddForm(false)
                    resetAddressForm()
                  }}
                >
                  Back
                </button>

                {isEditing ? (
                  <button
                    className="btn btn-default"
                    onClick={handleSubmit(handleEditSave)}
                  >
                    Save Edited Address
                  </button>
                ) : (
                  <button type="submit" className="btn btn-default">
                    Save Address
                  </button>
                )}
              </Modal.Footer>
            </Form>
          )}
        </Modal.Body>
      </Modal>
      <style jsx>{`
        .custom-radio .form-check-input {
          width: 24px;
          height: 24px;
          background-color: var(--custom-white);
          border: 1px solid var(--input-border);
        }
        .custom-radio .form-check-input:checked {
          width: 24px;
          height: 24px;
          background-color: var(--primary-color);
          border-color: var(--primary-color);
        }
      `}</style>
    </>
  )
}

export default AddressModal